/**
 * ShirtWerk — Startseiten-Komponenten (sw-*-Sections).
 * Ohne Abhängigkeiten, wird deferred in layout/theme.liquid geladen.
 *  - <sw-accordion>: FAQ, immer nur ein Eintrag offen (aria-expanded)
 *  - [data-sw-usp]: USP-Leiste rotiert auf Mobile
 *  - [data-sw-reveal]: dezentes Fade-up beim Einblenden
 *  - <sw-discount-tiers>: Mengenrabatt-Staffel aus dem Picura-Rechner (Produktseite)
 * Alle Animationen respektieren prefers-reduced-motion.
 */
(function () {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ---- FAQ-Accordion ---- */
  if (!customElements.get('sw-accordion')) {
    class SwAccordion extends HTMLElement {
      connectedCallback() {
        this.addEventListener('click', (event) => {
          const button = event.target.closest('button[aria-controls]');
          if (!button || !this.contains(button)) return;
          const willOpen = button.getAttribute('aria-expanded') !== 'true';
          this.querySelectorAll('button[aria-controls]').forEach((other) => {
            if (other !== button) this.toggle(other, false);
          });
          this.toggle(button, willOpen);
        });
      }

      toggle(button, open) {
        button.setAttribute('aria-expanded', String(open));
        const item = button.closest('.sw-faq__item');
        if (item) item.classList.toggle('is-open', open);
      }
    }
    customElements.define('sw-accordion', SwAccordion);
  }

  /* ---- USP-Leiste: auf Mobile ein Punkt nach dem anderen ---- */
  function initUsp(root) {
    if (root.dataset.swUspInit) return;
    root.dataset.swUspInit = 'true';
    const items = Array.from(root.querySelectorAll('.sw-usp__item'));
    if (items.length < 2) return;
    const mobile = window.matchMedia('(max-width: 749px)');
    let index = 0;
    let timer = null;

    const show = (next) => {
      items[index].classList.remove('is-active');
      index = next;
      items[index].classList.add('is-active');
    };
    const start = () => {
      if (timer || !mobile.matches || reducedMotion.matches) return;
      timer = window.setInterval(() => show((index + 1) % items.length), 4000);
    };
    const stop = () => {
      window.clearInterval(timer);
      timer = null;
    };

    mobile.addEventListener('change', () => (mobile.matches ? start() : stop()));
    root.addEventListener('focusin', stop);
    root.addEventListener('focusout', start);
    start();
  }

  /* ---- Fade-up beim Scrollen ---- */
  function initReveal() {
    const targets = document.querySelectorAll('[data-sw-reveal]:not(.is-visible)');
    if (!targets.length) return;
    const designMode = window.Shopify && window.Shopify.designMode;
    if (designMode || reducedMotion.matches || !('IntersectionObserver' in window)) {
      targets.forEach((el) => el.classList.add('is-visible'));
      return;
    }
    // Erst jetzt verstecken: fällt das Script aus, bleibt alles sichtbar.
    document.documentElement.classList.add('sw-reveal-ready');
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -6% 0px', threshold: 0 });
    targets.forEach((el) => observer.observe(el));
  }

  /* ---- Gestaffeltes Einblenden von Rastern ---- */
  function initStagger() {
    const groups = document.querySelectorAll('[data-sw-stagger]:not(.is-visible)');
    if (!groups.length) return;
    const designMode = window.Shopify && window.Shopify.designMode;
    const show = (group) => {
      const children = Array.from(group.children);
      children.forEach((child, i) => child.style.setProperty('--sw-i', Math.min(i, 8)));
      group.classList.add('is-visible');
      // Danach eigene Hover-Transitions der Karten wieder freigeben.
      window.setTimeout(() => group.classList.add('is-settled'), 520 + Math.min(children.length, 9) * 70);
    };
    if (designMode || reducedMotion.matches || !('IntersectionObserver' in window)) {
      groups.forEach((g) => { g.classList.add('is-visible', 'is-settled'); });
      return;
    }
    document.documentElement.classList.add('sw-reveal-ready');
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        show(entry.target);
        observer.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -6% 0px', threshold: 0 });
    groups.forEach((g) => observer.observe(g));
  }

  /* ---- Inhaltsverzeichnis + Lesefortschritt für lange Textseiten ---- */
  function initToc() {
    document.querySelectorAll('[data-sw-toc-root]').forEach((root) => {
      if (root.dataset.swTocInit) return;
      root.dataset.swTocInit = 'true';
      const nav = root.querySelector('[data-sw-toc]');
      const content = root.querySelector('[data-sw-toc-content]');
      if (!nav || !content) return;
      const level = root.dataset.swTocLevel || 'h2';
      const headings = Array.from(content.querySelectorAll(level)).filter((h) => h.textContent.trim());
      if (headings.length < 3) return;

      const slug = (text, i) => 'abschnitt-' + (i + 1) + '-' + text.toLowerCase()
        .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss')
        .replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 40);
      const list = document.createElement('ol');
      const links = headings.map((h, i) => {
        if (!h.id) h.id = slug(h.textContent, i);
        const li = document.createElement('li');
        const a = document.createElement('a');
        a.href = '#' + h.id;
        a.textContent = h.textContent.trim();
        li.appendChild(a);
        list.appendChild(li);
        return a;
      });

      // Eingeklappten Text vor dem Springen zum Abschnitt öffnen.
      list.addEventListener('click', () => {
        const collapse = root.querySelector('[data-sw-collapse]');
        if (collapse && collapse.classList.contains('is-collapsed')) setCollapsed(root, false);
      });

      const title = level === 'h2' ? 'Auf dieser Seite' : 'Inhalt';
      if (window.matchMedia('(max-width: 989px)').matches) {
        const details = document.createElement('details');
        const summary = document.createElement('summary');
        summary.textContent = title;
        details.append(summary, list);
        nav.appendChild(details);
      } else {
        const heading = document.createElement('p');
        heading.className = 'sw-doc__toc-title';
        heading.textContent = title;
        nav.append(heading, list);
      }
      nav.hidden = false;

      if ('IntersectionObserver' in window) {
        const setActive = (id) => links.forEach((a) => a.classList.toggle('is-active', a.hash === '#' + id));
        const io = new IntersectionObserver((entries) => {
          entries.forEach((e) => { if (e.isIntersecting) setActive(e.target.id); });
        }, { rootMargin: '-20% 0px -70% 0px' });
        headings.forEach((h) => io.observe(h));
      }

      if (root.dataset.swTocProgress === 'false') return;

      // Lesefortschritt am oberen Bildschirmrand
      const bar = document.createElement('div');
      bar.className = 'sw-progress';
      bar.setAttribute('aria-hidden', 'true');
      document.body.appendChild(bar);
      let ticking = false;
      const update = () => {
        const rect = content.getBoundingClientRect();
        const total = rect.height - window.innerHeight * 0.6;
        const progress = Math.min(1, Math.max(0, -rect.top / (total || 1)));
        bar.style.setProperty('--sw-progress', progress.toFixed(3));
        ticking = false;
      };
      window.addEventListener('scroll', () => {
        if (!ticking) { ticking = true; window.requestAnimationFrame(update); }
      }, { passive: true });
      update();
    });
  }

  /* ---- Einklappbarer Langtext („Weiterlesen“) ---- */
  function setCollapsed(root, collapsed) {
    const body = root.querySelector('[data-sw-collapse]');
    const toggle = root.querySelector('[data-sw-collapse-toggle]');
    if (!body || !toggle) return;
    body.classList.toggle('is-collapsed', collapsed);
    toggle.setAttribute('aria-expanded', String(!collapsed));
    toggle.querySelector('[data-sw-collapse-label]').textContent = collapsed ? toggle.dataset.labelMore : toggle.dataset.labelLess;
  }

  function initCollapse() {
    document.querySelectorAll('[data-sw-collapse]').forEach((body) => {
      if (body.dataset.swCollapseInit) return;
      body.dataset.swCollapseInit = 'true';
      const root = body.closest('[data-sw-toc-root]') || body.parentElement;
      const toggle = root.querySelector('[data-sw-collapse-toggle]');
      if (!toggle) return;
      const limit = parseInt(getComputedStyle(body).getPropertyValue('--sw-seo-collapsed'), 10) || 420;
      if (body.scrollHeight <= limit + 80) return; // kurzer Text: nichts einklappen
      toggle.hidden = false;
      setCollapsed(root, true);
      toggle.addEventListener('click', () => {
        const willCollapse = !body.classList.contains('is-collapsed');
        setCollapsed(root, willCollapse);
        if (willCollapse) body.closest('section').scrollIntoView({ behavior: reducedMotion.matches ? 'auto' : 'smooth', block: 'start' });
      });
    });
  }

  /* ---- Branchen-Tabs (role=tablist, Pfeiltasten) ---- */
  if (!customElements.get('sw-tabs')) {
    customElements.define('sw-tabs', class extends HTMLElement {
      connectedCallback() {
        const list = this.querySelector('[data-sw-tablist]');
        this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
        this.panels = Array.from(this.querySelectorAll('[data-sw-tabpanel]'));
        if (!list || this.tabs.length < 2) return;
        list.hidden = false;
        this.classList.add('is-enhanced');
        this.select(0, false);
        this.tabs.forEach((tab, i) => {
          tab.addEventListener('click', () => this.select(i, true));
          tab.addEventListener('keydown', (e) => {
            const dir = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
            if (!dir) return;
            e.preventDefault();
            this.select((i + dir + this.tabs.length) % this.tabs.length, true);
          });
        });
      }
      select(index, focus) {
        this.tabs.forEach((tab, i) => {
          const active = i === index;
          tab.setAttribute('aria-selected', String(active));
          tab.tabIndex = active ? 0 : -1;
          this.panels[i].hidden = !active;
          if (active && focus) tab.focus();
        });
      }
    });
  }

  /* ---- Formular vorbelegen (Branche / Planer → Anfrageformular) ---- */
  function prefillForm(values) {
    const anyField = document.querySelector('[data-sw-field-product], [data-sw-field-branch]');
    const form = document.getElementById('B2BRequestForm') || (anyField && anyField.closest('form'));
    if (!form) return;
    const setSelect = (sel, value) => {
      const el = form.querySelector(sel);
      if (!el || !value) return;
      const v = value.toLowerCase();
      const opts = Array.from(el.options).filter((o) => o.value);
      const match = opts.find((o) => o.value.toLowerCase() === v)
        || opts.find((o) => v.indexOf(o.value.toLowerCase()) === 0 || o.value.toLowerCase().indexOf(v) === 0);
      if (match) el.value = match.value;
    };
    setSelect('[data-sw-field-branch]', values.branch);
    if (values.products && values.products.length) {
      form.querySelectorAll('[data-sw-field-product]').forEach((cb) => {
        cb.checked = values.products.some((v) => v.toLowerCase() === cb.dataset.value.toLowerCase());
      });
    }
    setSelect('[data-sw-field-position]', values.position);
    if (values.qty) {
      const radios = Array.from(form.querySelectorAll('[data-sw-field-qty]'));
      const q = Number(values.qty);
      const pick = radios.find((r) => {
        const nums = r.value.match(/\d+/g) || [];
        const from = Number(nums[0] || 0);
        const to = nums[1] ? Number(nums[1]) : Infinity;
        return q >= from && q <= to;
      }) || radios[radios.length - 1];
      if (pick) pick.checked = true;
      const msg = form.querySelector('[data-sw-field-message]');
      if (msg && !msg.value.trim()) msg.value = 'Geplante Menge: ca. ' + q + ' Stück';
    }
  }
  document.addEventListener('click', (e) => {
    const branchLink = e.target.closest('[data-sw-prefill-branch]');
    if (branchLink) prefillForm({ branch: branchLink.dataset.swPrefillBranch });
  });

  /* ---- Mengenplaner ---- */
  if (!customElements.get('sw-planner')) {
    customElements.define('sw-planner', class extends HTMLElement {
      connectedCallback() {
        this.range = this.querySelector('[data-sw-qty]');
        if (!this.range) return;
        this.out = this.querySelector('[data-sw-qty-out]');
        this.meter = this.querySelector('[data-sw-meter]');
        this.tiers = Array.from(this.querySelectorAll('[data-sw-tier]'));
        this.querySelectorAll('[data-sw-num]').forEach((el) => {
          const n = Number(el.textContent);
          if (!Number.isNaN(n)) el.textContent = n.toLocaleString('de-DE');
        });
        this.range.addEventListener('input', () => this.update());
        const submit = this.querySelector('[data-sw-planner-submit]');
        if (submit) submit.addEventListener('click', () => {
          const products = Array.from(this.querySelectorAll('[data-sw-product]:checked')).map((c) => c.value);
          const position = this.querySelector('[data-sw-position]:checked');
          prefillForm({ qty: this.range.value, products, position: position && position.value });
        });
        this.update();
      }
      update() {
        const min = Number(this.range.min), max = Number(this.range.max), q = Number(this.range.value);
        const pct = ((q - min) / (max - min)) * 100;
        this.range.style.setProperty('--sw-fill', pct + '%');
        this.range.setAttribute('aria-valuetext', q + ' Stück');
        if (this.out) this.out.textContent = q.toLocaleString('de-DE') + (q >= max ? '+' : '');
        if (this.meter) this.meter.style.width = Math.max(8, pct) + '%';
        const maxNote = this.querySelector('[data-sw-max-note]');
        if (maxNote) maxNote.hidden = q < max;
        let shown = false;
        this.tiers.forEach((t) => {
          const from = Number(t.dataset.from), to = Number(t.dataset.to) || Infinity;
          const match = !shown && q >= from && q <= to;
          t.hidden = !match;
          if (match) shown = true;
        });
        if (!shown && this.tiers.length) this.tiers[this.tiers.length - 1].hidden = false;
      }
    });
  }

  /* ---- Mehrstufiges Formular ---- */
  if (!customElements.get('sw-steps')) {
    customElements.define('sw-steps', class extends HTMLElement {
      connectedCallback() {
        this.steps = Array.from(this.querySelectorAll('[data-sw-step]'));
        if (this.steps.length < 2) return;
        this.progress = this.querySelector('[data-sw-progress]');
        this.prev = this.querySelector('[data-sw-prev]');
        this.next = this.querySelector('[data-sw-next]');
        this.submit = this.querySelector('[data-sw-submit]');
        this.classList.add('is-enhanced');
        if (this.progress) this.progress.hidden = false;
        this.addEventListener('change', (e) => {
          const g = e.target.closest('[data-sw-require-one]');
          if (g) g.querySelector('input[type="checkbox"]').setCustomValidity('');
        });
        this.prev.addEventListener('click', () => this.go(this.index - 1));
        this.next.addEventListener('click', () => {
          if (this.valid(this.index)) this.go(this.index + 1);
        });
        this.go(0, false);
      }
      valid(i) {
        const groups = Array.from(this.steps[i].querySelectorAll('[data-sw-require-one]'));
        for (const g of groups) {
          const boxes = Array.from(g.querySelectorAll('input[type="checkbox"]'));
          boxes[0].setCustomValidity(boxes.some((b) => b.checked) ? '' : g.dataset.swRequireOne);
          if (!boxes[0].checkValidity()) { boxes[0].reportValidity(); return false; }
        }
        const fields = Array.from(this.steps[i].querySelectorAll('input, select, textarea'));
        for (const f of fields) {
          if (!f.checkValidity()) { f.reportValidity(); return false; }
        }
        return true;
      }
      go(i, focus = true) {
        this.index = Math.max(0, Math.min(i, this.steps.length - 1));
        this.steps.forEach((s, n) => { s.hidden = n !== this.index; });
        const last = this.index === this.steps.length - 1;
        this.prev.hidden = this.index === 0;
        this.next.hidden = last;
        this.submit.hidden = !last;
        if (this.progress) {
          Array.from(this.progress.children).forEach((li, n) => {
            li.classList.toggle('is-active', n === this.index);
            li.classList.toggle('is-done', n < this.index);
          });
          this.progress.style.setProperty('--sw-progress-step', (this.index + 1) / this.steps.length);
        }
        if (focus) {
          const first = this.steps[this.index].querySelector('input, select, textarea');
          if (first) first.focus({ preventScroll: true });
        }
      }
    });
  }

  /* ---- Kennzahlen hochzählen ---- */
  function initCounters() {
    const nums = document.querySelectorAll('[data-sw-count]:not([data-sw-counted])');
    if (!nums.length || reducedMotion.matches || !('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        io.unobserve(entry.target);
        const el = entry.target;
        const raw = el.dataset.swCount;
        const target = parseInt(raw.replace(/\D/g, ''), 10);
        if (!target || /[^\d.]/.test(raw)) return; // z. B. „3–5“ oder „DTF“ bleiben unverändert
        const start = performance.now();
        const tick = (t) => {
          const p = Math.min(1, (t - start) / 900);
          el.textContent = Math.round(target * (1 - Math.pow(1 - p, 3))).toLocaleString('de-DE');
          if (p < 1) requestAnimationFrame(tick); else el.textContent = raw;
        };
        requestAnimationFrame(tick);
      });
    }, { threshold: 0.4 });
    nums.forEach((n) => { n.dataset.swCounted = 'true'; io.observe(n); });
  }

  /* ---- Schwebender Versand-Hinweis ---- */
  if (!customElements.get('sw-ship-float')) {
    customElements.define('sw-ship-float', class extends HTMLElement {
      connectedCallback() {
        this.pill = this.querySelector('.sw-float__pill');
        this.panel = this.querySelector('.sw-float__panel');
        this.threshold = Number(this.dataset.threshold) || 5000;
        this.fmt = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });
        this.pill.addEventListener('click', () => this.toggle(true));
        this.querySelector('[data-sw-float-close]').addEventListener('click', () => this.toggle(false));
        this.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.toggle(false); });
        document.addEventListener('cart:updated', () => this.refresh());
        document.addEventListener('visibilitychange', () => { if (!document.hidden) this.refresh(); });
        window.addEventListener('pageshow', (e) => { if (e.persisted) this.refresh(); });
        requestAnimationFrame(() => this.classList.add('is-ready'));
      }
      toggle(open) {
        this.panel.hidden = !open;
        this.pill.hidden = open;
        this.pill.setAttribute('aria-expanded', String(open));
        this.classList.toggle('is-open', open);
        if (open) { this.refresh(); this.querySelector('[data-sw-float-close]').focus(); } else { this.pill.focus(); }
      }
      async refresh() {
        try {
          const cart = await window.ShirtWerk.fetchCart();
          this.render(cart.total_price);
        } catch (e) { /* Hinweis bleibt beim letzten Stand */ }
      }
      render(total) {
        const remaining = this.threshold - total;
        const reached = remaining <= 0;
        const pct = Math.min(100, Math.round((total / this.threshold) * 100));
        const money = (c) => this.fmt.format(c / 100);
        this.classList.toggle('is-reached', reached);
        this.querySelector('[data-sw-float-fill]').style.setProperty('--sw-ship', pct + '%');
        this.querySelector('[data-sw-float-bar]').setAttribute('aria-valuenow', pct);
        this.querySelector('[data-sw-float-total]').textContent = money(total);
        this.querySelector('[data-sw-float-title]').textContent = reached
          ? 'Dein Versand ist kostenlos!' : `Nur noch ${money(remaining)} bis zum kostenlosen Versand`;
        this.querySelector('[data-sw-float-sub]').innerHTML = reached
          ? 'Du hast den Mindestbestellwert erreicht.' : 'Warenkorb füllen &amp; <strong>Versandkosten sparen</strong>';
        this.querySelector('[data-sw-float-pill]').textContent = reached
          ? 'Versand kostenlos' : `Kostenloser Versand ab ${money(this.threshold).replace(',00', '')}`;
      }
    });
  }

  /* ---- Mengenrabatt-Staffel auf der Produktseite ----
     Liest die Staffel, die der Picura-Rechner für dieses Produkt hat (gleicher
     App-Proxy-Aufruf wie der Rechner selbst), und ersetzt damit die Startwerte
     aus den Block-Einstellungen. Ohne Rabatt im Rechner verschwindet das Banner,
     damit kein Rabatt versprochen wird, den der Preis nicht enthält. */
  if (!customElements.get('sw-discount-tiers')) {
    customElements.define('sw-discount-tiers', class extends HTMLElement {
      connectedCallback() {
        if (this.dataset.swTiersInit) return;
        this.dataset.swTiersInit = 'true';
        this.load();
      }
      async load() {
        let res;
        try {
          res = await fetch(`${this.dataset.proxyUrl}/calculator/${this.dataset.productId}`, {
            headers: { Accept: 'application/json' },
          });
        } catch (e) { return; /* Netz weg → Startwerte bleiben */ }
        if (res.status === 404) { this.hidden = true; return; } // Produkt ohne Rechner
        if (!res.ok) return;
        let cfg;
        try { cfg = (await res.json()).data; } catch (e) { return; }
        if (!cfg) return;
        if (cfg.status && cfg.status !== 'PUBLISHED') { this.hidden = true; return; } // Rechner selbst blendet sich dann aus
        const d = cfg.deliveryOptions || (cfg.config && cfg.config.deliveryOptions) || {};
        const mode = d.discountMode;
        const tiers = (d.discountTiers || [])
          .filter((t) => t && t.from > 0 && t.percent > 0)
          .sort((a, b) => a.from - b.from);
        if ((mode !== 'quantity' && mode !== 'sqm') || !tiers.length) { this.hidden = true; return; }
        this.render(tiers, mode === 'sqm' ? 'm²' : 'Stück');
      }
      render(tiers, unit) {
        const num = (n) => Number(n).toLocaleString('de-DE', { maximumFractionDigits: 2 });
        const max = Math.max(...tiers.map((t) => t.percent));
        const title = this.querySelector('[data-sw-tiers-title]');
        if (title) title.textContent = `Bis zu ${num(max)} % Mengenrabatt`;
        const list = this.querySelector('[data-sw-tiers-list]');
        if (!list) return;
        const items = tiers.map((t) => [`−${num(t.percent)} %`, `ab ${num(t.from)} ${unit}`]);
        // Nur bei echter Abweichung neu zeichnen, sonst springt nichts
        const flat = (s) => s.replace(/\s+/g, '');
        if (flat(list.textContent) === flat(items.flat().join(''))) return;
        list.replaceChildren(...items.map(([pct, qty]) => {
          const li = document.createElement('li');
          li.className = 'pdp-tiers__item';
          const strong = document.createElement('strong');
          strong.className = 'pdp-tiers__pct';
          strong.textContent = pct;
          const span = document.createElement('span');
          span.className = 'pdp-tiers__qty';
          span.textContent = qty;
          li.append(strong, span);
          return li;
        }));
        list.style.setProperty('--tier-count', tiers.length);
      }
    });
  }

  function init() {
    initCounters();
    initCollapse();
    document.querySelectorAll('[data-sw-usp]').forEach(initUsp);
    initReveal();
    initStagger();
    initToc();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme Editor: nach dem Neuladen einer Section erneut initialisieren.
  document.addEventListener('shopify:section:load', init);
})();
